/**
 * Referral tracking utilities
 *
 * Handles storing and retrieving referral codes using both localStorage
 * AND cookies as backup for better cross-browser compatibility
 * (some mobile browsers restrict localStorage in private mode)
 */

import logger from "./logger";

const REFERRAL_STORAGE_KEY = "cluck_referral";
const REFERRAL_COOKIE_NAME = "cluck_ref";
const DEFAULT_EXPIRY_HOURS = 24;

// ============ COOKIE HELPERS ============

/**
 * Set a cookie with expiry
 */
function setCookie(name, value, expiryHours) {
  try {
    const expires = new Date(
      Date.now() + expiryHours * 60 * 60 * 1000,
    ).toUTCString();
    // Use SameSite=Lax for cross-site navigation compatibility
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  } catch (e) {
    logger.warn("Failed to set cookie:", e);
  }
}

/**
 * Get a cookie value
 */
function getCookie(name) {
  try {
    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const [cookieName, cookieValue] = cookie.trim().split("=");
      if (cookieName === name) {
        return decodeURIComponent(cookieValue);
      }
    }
    return null;
  } catch (e) {
    logger.warn("Failed to get cookie:", e);
    return null;
  }
}

/**
 * Delete a cookie
 */
function deleteCookie(name) {
  try {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  } catch (e) {
    logger.warn("Failed to delete cookie:", e);
  }
}

// ============ CONFIG FETCH ============

/**
 * Fetch public referral config (cookie expiry hours)
 * @returns {Promise<{cookieExpiryHours: number, isActive: boolean}>}
 */
async function fetchPublicReferralConfig() {
  try {
    const response = await fetch("/api/casino/referrals/public-config");
    if (!response.ok)
      return { cookieExpiryHours: DEFAULT_EXPIRY_HOURS, isActive: true };
    return await response.json();
  } catch (e) {
    logger.warn("Failed to fetch referral config:", e);
    return { cookieExpiryHours: DEFAULT_EXPIRY_HOURS, isActive: true };
  }
}

// ============ STORAGE FUNCTIONS ============

/**
 * Store a referral code in both localStorage AND cookie
 * @param {string} code - Referral code from URL
 * @param {number} expiryHours - Hours until expiry (default 24)
 */
export function storeReferralCode(code, expiryHours = DEFAULT_EXPIRY_HOURS) {
  if (!code) return;

  const upperCode = code.toUpperCase();

  // Store in cookie (primary - more reliable on mobile)
  setCookie(REFERRAL_COOKIE_NAME, upperCode, expiryHours);

  // Store in localStorage with timestamp (backup with expiry tracking)
  const referralData = {
    code: upperCode,
    timestamp: Date.now(),
    expiryHours,
  };

  try {
    localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify(referralData));
  } catch (e) {
    // localStorage may be blocked - cookie is primary anyway
    logger.warn(
      "Failed to store referral in localStorage (cookie backup active):",
      e,
    );
  }
}

/**
 * Get stored referral code if still valid
 * Checks localStorage first, falls back to cookie
 * @returns {string|null} Referral code or null if expired/not found
 */
export function getStoredReferralCode() {
  // Try localStorage first (has expiry tracking)
  try {
    const stored = localStorage.getItem(REFERRAL_STORAGE_KEY);
    if (stored) {
      const referralData = JSON.parse(stored);
      const { code, timestamp, expiryHours } = referralData;

      // Check if expired
      const expiryMs = (expiryHours || DEFAULT_EXPIRY_HOURS) * 60 * 60 * 1000;
      const isExpired = Date.now() - timestamp > expiryMs;

      if (!isExpired && code) {
        return code;
      }

      // Expired - clear it
      clearReferralCode();
      return null;
    }
  } catch (e) {
    logger.warn("Failed to get referral from localStorage:", e);
  }

  // Fallback to cookie (browser handles expiry)
  const cookieCode = getCookie(REFERRAL_COOKIE_NAME);
  if (cookieCode) {
    return cookieCode;
  }

  return null;
}

/**
 * Clear stored referral code from both localStorage and cookie
 */
export function clearReferralCode() {
  try {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch (e) {
    logger.warn("Failed to clear referral from localStorage:", e);
  }

  deleteCookie(REFERRAL_COOKIE_NAME);
}

/**
 * Check URL for referral code and store it
 * Call this on app initialization
 * Fetches config from backend to get admin-configured expiry hours
 */
export async function captureReferralFromURL() {
  if (typeof window === "undefined") return;

  const urlParams = new URLSearchParams(window.location.search);
  const refCode = urlParams.get("ref");

  if (refCode) {
    const upper = refCode.toUpperCase();
    // Marketing source codes (SRC-) and affiliate codes (AFF-) attribute paid/ad
    // and partner traffic, where a signup routinely lands days after the click.
    // Give them a 30-day window to match the acquisition tracker
    // (acquisitionUtils EXPIRY_DAYS) instead of the short refer-a-friend window,
    // and capture them independently of the refer-a-friend program toggle. Under
    // the old behaviour these rode the friend-referral path: gated on
    // config.isActive and stored for only ~24h, so delayed ad conversions were
    // dropped from source-code attribution (the acquisition report still caught
    // them, which is why the two never matched).
    const isCampaignCode = upper.startsWith("SRC-") || upper.startsWith("AFF-");

    if (isCampaignCode) {
      storeReferralCode(refCode, 30 * 24);
      logger.info("Campaign source code captured:", upper);
    } else {
      // Genuine refer-a-friend code: keep the admin-configured expiry + the
      // program active gate (its reward logic depends on both).
      const config = await fetchPublicReferralConfig();
      if (config.isActive) {
        storeReferralCode(refCode, config.cookieExpiryHours);
        logger.info("Referral code captured:", upper);
      }
    }

    // Remove ref param from URL without reload (cleaner UX)
    urlParams.delete("ref");
    const newSearch = urlParams.toString();
    const newUrl =
      window.location.pathname +
      (newSearch ? "?" + newSearch : "") +
      window.location.hash;
    window.history.replaceState({}, "", newUrl);
  }
}
